import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import api from '../services/api';
import { ReverseFeedback } from '../types';
import PageContainer from '../components/ui/PageContainer';

const Section: React.FC<{ kicker: string; title: string; children: React.ReactNode }> = ({
  kicker,
  title,
  children,
}) => (
  <Paper sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ mb: 2 }}>
      <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
        {kicker}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

const Metric: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Paper sx={{ p: 2, bgcolor: 'action.hover', height: '100%' }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em' }}
    >
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
      {value}
    </Typography>
  </Paper>
);

const TextCard: React.FC<{ title: string; text?: string; highlight?: boolean }> = ({ title, text, highlight }) => (
  <Paper
    sx={{
      p: 2,
      height: '100%',
      bgcolor: highlight ? 'action.hover' : 'transparent',
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
      {text}
    </Typography>
  </Paper>
);

const ReverseFeedbackDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<ReverseFeedback | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadFeedback = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const response = await api.get<ReverseFeedback>(`/feedbacks/reverse/${id}`);
      setFeedback(response.data);
    } catch (err: any) {
      setError('Erro ao carregar feedback reverso');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadFeedback();
    }
  }, [id, loadFeedback]);

  const formatDate = useCallback(
    (value?: string): string =>
      value ? new Date(value).toLocaleDateString('pt-BR') : 'Não informado',
    [],
  );

  const formatAnswer = useCallback((value?: string): string => {
    if (!value) return 'Não informado';
    if (value === 'sim') return 'Sim';
    if (value === 'parcialmente') return 'Parcialmente';
    return 'Não';
  }, []);

  const summaryCards = useMemo(
    () =>
      feedback
        ? [
            {
              label: 'Score final',
              value: feedback.finalScore,
              description: 'Resultado consolidado do feedback reverso.',
            },
            {
              label: 'Ciclo',
              value: feedback.cycle?.name || 'Não definido',
              description: 'Período vinculado à devolutiva.',
            },
            {
              label: 'Anonimato',
              value: feedback.anonymous ? 'Sim' : 'Não',
              description: 'Forma escolhida para envio do retorno.',
            },
          ]
        : [],
    [feedback],
  );

  if (loading) {
    return (
      <PageContainer>
        <Stack spacing={2} sx={{ py: 8, alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography color="text.secondary">Preparando feedback reverso…</Typography>
        </Stack>
      </PageContainer>
    );
  }

  if (error || !feedback) {
    return (
      <PageContainer title="Feedback reverso não encontrado">
        <Paper sx={{ p: 4, border: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ mb: 2 }}>{error || 'Feedback reverso não encontrado'}</Typography>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/feedbacks')}>
            Voltar para feedbacks
          </Button>
        </Paper>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      icon={<DescriptionOutlinedIcon />}
      title="Detalhes da devolutiva"
      subtitle="Consulte a percepção sobre clareza, suporte, desenvolvimento e sugestões práticas para fortalecer a liderança no próximo ciclo."
      actions={
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/feedbacks')}>
          Voltar
        </Button>
      }
    >
      <Stack spacing={3}>
        <Grid container spacing={2}>
          {summaryCards.map((card) => (
            <Grid key={card.label} size={{ xs: 12, sm: 4 }}>
              <Paper sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 700 }}>
                  {card.label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, my: 0.5 }}>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Section kicker="Geral" title="Informações principais">
          <Grid container spacing={2}>
            {[
              {
                label: 'Desenvolvedor',
                value: feedback.anonymous ? 'Anônimo' : feedback.developer?.name || 'Não informado',
              },
              { label: 'Gestor', value: feedback.manager?.name || 'Não informado' },
              { label: 'Ciclo', value: feedback.cycle?.name || 'Não definido' },
              { label: 'Data', value: formatDate(feedback.createdAt) },
            ].map((info) => (
              <Grid key={info.label} size={{ xs: 12, sm: 6, md: 3 }}>
                <Metric label={info.label} value={info.value} />
              </Grid>
            ))}
          </Grid>
        </Section>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Section kicker="Clareza" title="Clareza técnica">
              <Grid container spacing={2} sx={{ mb: feedback.clarityObservations ? 2 : 0 }}>
                <Grid size={6}>
                  <Metric label="Score" value={`${feedback.clarityScore}/5`} />
                </Grid>
                <Grid size={6}>
                  <Metric label="Resposta" value={formatAnswer(feedback.clarityQuestion)} />
                </Grid>
              </Grid>
              {feedback.clarityObservations && (
                <TextCard title="Observações" text={feedback.clarityObservations} />
              )}
            </Section>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Section kicker="Suporte" title="Disponibilidade e suporte">
              <Grid container spacing={2} sx={{ mb: feedback.availabilityObservations ? 2 : 0 }}>
                <Grid size={6}>
                  <Metric label="Score" value={`${feedback.availabilityScore}/5`} />
                </Grid>
                <Grid size={6}>
                  <Metric label="Resposta" value={formatAnswer(feedback.availabilityQuestion)} />
                </Grid>
              </Grid>
              {feedback.availabilityObservations && (
                <TextCard title="Observações" text={feedback.availabilityObservations} />
              )}
            </Section>
          </Grid>
        </Grid>

        <Section kicker="Desenvolvimento" title="Qualidade do feedback e evolução">
          <Grid container spacing={2} sx={{ mb: feedback.feedbackQualityObservations ? 2 : 0 }}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Metric label="Score" value={`${feedback.feedbackQualityScore}/5`} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Metric label="Resposta" value={formatAnswer(feedback.feedbackQualityQuestion)} />
            </Grid>
          </Grid>
          {feedback.feedbackQualityObservations && (
            <TextCard title="Observações" text={feedback.feedbackQualityObservations} />
          )}
        </Section>

        <Section kicker="Consolidado" title="Síntese da devolutiva">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextCard title="O que o gestor faz bem" text={feedback.whatManagerDoesWell} highlight />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextCard title="O que pode melhorar" text={feedback.whatCanImprove} highlight />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextCard title="Sugestões práticas" text={feedback.suggestions} highlight />
            </Grid>
          </Grid>
        </Section>
      </Stack>
    </PageContainer>
  );
};

export default ReverseFeedbackDetail;
